import React from 'react';

// Função para converter HEX para RGB string
const hexToRgb = (hex) => {
    if (!hex || typeof hex !== 'string') return '13, 110, 253'; // Fallback
    let cleanHex = hex.startsWith('#') ? hex.slice(1) : hex;
    if (cleanHex.length === 3) {
        cleanHex = cleanHex.split('').map(char => char + char).join('');
    }
    if (cleanHex.length !== 6) return '13, 110, 253'; // Fallback
    const r = parseInt(cleanHex.substring(0, 2), 16);
    const g = parseInt(cleanHex.substring(2, 4), 16);
    const b = parseInt(cleanHex.substring(4, 6), 16);
    return `${r}, ${g}, ${b}`;
};

// Função para determinar se uma cor é escura, para contraste do texto
const isColorDark = (hex) => {
    if (!hex) return true;
    const [r, g, b] = hexToRgb(hex).split(',').map(Number);
    const yiq = ((r * 299) + (g * 587) + (b * 114)) / 1000;
    return yiq < 128;
};

// Função para clarear ou escurecer uma cor HEX
const shadeColor = (hex, percent) => {
    if (!hex) return '#0b5ed7'; // Fallback
    let [r, g, b] = hexToRgb(hex).split(',').map(Number);
    const t = percent < 0 ? 0 : 255;
    const p = percent < 0 ? percent * -1 : percent;
    r = Math.round((t - r) * p) + r;
    g = Math.round((t - g) * p) + g;
    b = Math.round((t - b) * p) + b;

    r = Math.max(0, Math.min(255, r));
    g = Math.max(0, Math.min(255, g));
    b = Math.max(0, Math.min(255, b));

    return `#${(((r << 16) | (g << 8)) | b).toString(16).padStart(6, '0')}`;
};

function GlobalStyles({ brandColorLight, brandColorDark }) {
    const finalBrandColorLight = brandColorLight || '#0d6efd';
    const finalBrandColorDark = brandColorDark || '#5865F2';

    // --- Cálculos para o Tema Claro ---
    const lightRgb = hexToRgb(finalBrandColorLight);
    const lightThemeTextColor = isColorDark(finalBrandColorLight) ? '#FFFFFF' : '#212529';
    const lightHoverAmount = isColorDark(finalBrandColorLight) ? 0.10 : -0.10;
    const lightActiveAmount = isColorDark(finalBrandColorLight) ? 0.15 : -0.15;

    // --- Cálculos para o Tema Escuro ---
    const darkRgb = hexToRgb(finalBrandColorDark);
    const darkThemeTextColor = isColorDark(finalBrandColorDark) ? '#FFFFFF' : '#212529';
    const darkHoverAmount = isColorDark(finalBrandColorDark) ? 0.10 : -0.10;
    const darkActiveAmount = isColorDark(finalBrandColorDark) ? 0.15 : -0.15;

    const styles = `
        :root {
            --bs-border-radius: 0;
            --bs-border-radius-sm: 0;
            --bs-border-radius-lg: 0;
            --bs-border-radius-xl: 0;
            --bs-border-radius-xxl: 0;
            --bs-border-radius-2xl: 0;
            --bs-border-radius-pill: 0;
        }
        .rounded-circle {
            border-radius: 50% !important;
        }
        img.rounded-circle {
             border-radius: 50% !important;
        }

        /* Tema Claro */
        [data-bs-theme="light"] {
            --bs-primary: ${finalBrandColorLight};
            --bs-primary-rgb: ${lightRgb};
            --bs-primary-contrast: ${lightThemeTextColor};
            --bs-primary-text-emphasis: ${shadeColor(finalBrandColorLight, -0.4)};
            --bs-primary-bg-subtle: ${shadeColor(finalBrandColorLight, 0.8)};
            --bs-primary-border-subtle: ${shadeColor(finalBrandColorLight, 0.6)};
            
            --bs-link-color: ${finalBrandColorLight};
            --bs-link-color-rgb: ${lightRgb};
            --bs-link-hover-color: ${shadeColor(finalBrandColorLight, lightHoverAmount)};
            --bs-link-hover-color-rgb: ${hexToRgb(shadeColor(finalBrandColorLight, lightHoverAmount))};
        }

        /* Tema Escuro */
        [data-bs-theme="dark"] {
            --bs-primary: ${finalBrandColorDark};
            --bs-primary-rgb: ${darkRgb};
            --bs-primary-contrast: ${darkThemeTextColor};
            --bs-primary-text-emphasis: ${shadeColor(finalBrandColorDark, 0.4)};
            --bs-primary-bg-subtle: ${shadeColor(finalBrandColorDark, -0.8)};
            --bs-primary-border-subtle: ${shadeColor(finalBrandColorDark, -0.6)};

            --bs-link-color: ${finalBrandColorDark};
            --bs-link-color-rgb: ${darkRgb};
            --bs-link-hover-color: ${shadeColor(finalBrandColorDark, darkHoverAmount)};
            --bs-link-hover-color-rgb: ${hexToRgb(shadeColor(finalBrandColorDark, darkHoverAmount))};
        }
        
        /* Estilos de Botão Primário */
        .btn-primary {
            color: var(--bs-primary-contrast);
            background-color: var(--bs-primary);
            border-color: var(--bs-primary);
        }

        [data-bs-theme="light"] .btn-primary:hover {
            background-color: ${shadeColor(finalBrandColorLight, lightHoverAmount)};
            border-color: ${shadeColor(finalBrandColorLight, lightHoverAmount)};
        }
        [data-bs-theme="light"] .btn-primary:active {
            background-color: ${shadeColor(finalBrandColorLight, lightActiveAmount)};
            border-color: ${shadeColor(finalBrandColorLight, lightActiveAmount)};
        }

        [data-bs-theme="dark"] .btn-primary:hover {
            background-color: ${shadeColor(finalBrandColorDark, darkHoverAmount)};
            border-color: ${shadeColor(finalBrandColorDark, darkHoverAmount)};
        }
        [data-bs-theme="dark"] .btn-primary:active {
            background-color: ${shadeColor(finalBrandColorDark, darkActiveAmount)};
            border-color: ${shadeColor(finalBrandColorDark, darkActiveAmount)};
        }

        /* Estilos do Selo (Badge) */
        .badge.bg-primary {
            color: var(--bs-primary-contrast) !important;
        }

        /* Estilos da Sidebar */
        aside.bg-body-tertiary .nav-link.active {
            background-color: var(--bs-primary) !important;
            color: var(--bs-primary-contrast) !important;
        }

        aside.bg-body-tertiary .nav-link.active svg {
            color: inherit !important;
        }

        /* CORREÇÃO PARA TABELAS EM MODO ESCURO */
        [data-bs-theme="dark"] .table-light {
            --bs-table-bg: #2b3035;
            --bs-table-color: #dee2e6;
            border-color: #373b3e;
        }

        /* CORREÇÃO PARA O BOTÃO DE UPLOAD DE ARQUIVO (AMBOS OS TEMAS) */
        .form-control::file-selector-button {
            color: var(--bs-body-color);
            background-color: var(--bs-tertiary-bg);
            border-color: var(--bs-border-color);
            transition: color .15s ease-in-out,background-color .15s ease-in-out,border-color .15s ease-in-out,box-shadow .15s ease-in-out;
        }

        .form-control:hover:not(:disabled):not([readonly])::file-selector-button {
            background-color: var(--bs-secondary-bg);
        }
    `;

    return <style>{styles}</style>;
}

export default GlobalStyles;
